import React from 'react';

export type RuleStyle = 'double' | 'double-subtle' | 'single' | 'dotted' | 'dashed';
export type RuledHeaderSize = 'sm' | 'md' | 'lg';

export interface RuledHeaderProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  metaRight?: React.ReactNode;
  ruleStyle?: RuleStyle;
  size?: RuledHeaderSize;
  uppercase?: boolean;
  className?: string;
}

export const RuledHeader: React.FC<RuledHeaderProps> = ({
  title,
  subtitle,
  metaRight,
  ruleStyle = 'double',
  size = 'md',
  uppercase = true,
  className = '',
}) => {
  const ruleClasses: Record<RuleStyle, string> = {
    double: 'ruled-double',
    'double-subtle': 'ruled-double-subtle',
    single: 'border-t border-b border-paper-400 py-2',
    dotted: 'border-t border-b border-dotted border-ink-400 py-2',
    dashed: 'border-t border-b border-dashed border-paper-400 py-2',
  };

  const sizeClasses: Record<RuledHeaderSize, { title: string; subtitle: string }> = {
    sm: {
      title: 'text-sm sm:text-base font-bold',
      subtitle: 'text-[11px]',
    },
    md: {
      title: 'text-lg sm:text-xl font-bold',
      subtitle: 'text-xs',
    },
    lg: {
      title: 'text-xl sm:text-2xl font-black',
      subtitle: 'text-xs sm:text-sm',
    },
  };

  return (
    <div
      className={`
        w-full flex items-center justify-between gap-4 select-none
        ${ruleClasses[ruleStyle]}
        ${className}
      `}
    >
      <div className="min-w-0 space-y-0.5">
        {typeof title === 'string' ? (
          <h2
            className={`
              font-serif tracking-wider text-ink-900 leading-tight
              ${sizeClasses[size].title}
              ${uppercase ? 'uppercase' : ''}
            `}
          >
            {title}
          </h2>
        ) : (
          title
        )}
        {subtitle && (
          <p
            className={`
              font-serif text-ink-500 tracking-widest
              ${sizeClasses[size].subtitle}
            `}
          >
            {subtitle}
          </p>
        )}
      </div>

      {metaRight && (
        <div className="shrink-0 flex items-center gap-2 font-serif text-xs text-ink-700">
          {metaRight}
        </div>
      )}
    </div>
  );
};
